import { useEffect, useState } from 'react';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';
import { getRatioFromMediaValue } from './getRatioFromMedia';
import { usePostSettings } from './usePostSettings';

interface UsePopupMediaRatioProps {
  defaultRatio: number;
  loadedRatio: number | undefined;
}

const PLATFORMS_WITH_UNDEFINED_RATIO: string[] = [
  'tiktok',
  'twitter',
  'instagram'
];

export function usePopupMediaRatio({
  defaultRatio,
  loadedRatio
}: UsePopupMediaRatioProps): number | undefined {
  const { layout } = useSocialFeedSettings();
  const {
    isInPopup,
    media: rawMedia,
    source: { type: platform }
  } = usePost();
  const { postImageRatio: settingsRatio } = usePostSettings();

  const nativeRatio = getRatioFromMediaValue(rawMedia);
  const canDeriveRatio = nativeRatio !== undefined;

  const isVideoWithUndefinedRatio =
    rawMedia?.type === 'video' &&
    PLATFORMS_WITH_UNDEFINED_RATIO.includes(platform);
  const needsProbing =
    isInPopup && !canDeriveRatio && !isVideoWithUndefinedRatio;
  const mediaSrc = rawMedia?.thumbnail || rawMedia?.src;
  const thumbnailSrc =
    needsProbing && typeof mediaSrc === 'string' ? mediaSrc : undefined;

  const probedRatio = useProbedImageRatio(thumbnailSrc);

  if (!isInPopup) {
    const derivedRatio = settingsRatio || defaultRatio;

    return {
      grid: derivedRatio,
      list: defaultRatio,
      carousel: derivedRatio,
      slider: defaultRatio,
      masonry: undefined
    }[layout];
  }

  if (isVideoWithUndefinedRatio) {
    return undefined;
  }

  if (loadedRatio) {
    return loadedRatio;
  }

  if (canDeriveRatio) {
    return nativeRatio || 1;
  }

  if (needsProbing) {
    return probedRatio || 1;
  }

  return undefined;
}

function probeCachedImageRatio(src: string): number | undefined {
  const img = new Image();
  img.src = src;

  if (img.complete && img.naturalWidth && img.naturalHeight) {
    return img.naturalHeight / img.naturalWidth;
  }

  return undefined;
}

function useProbedImageRatio(src: string | undefined): number | undefined {
  const [ratio, setRatio] = useState<number | undefined>(() =>
    src ? probeCachedImageRatio(src) : undefined
  );

  useEffect(() => {
    if (!src || ratio !== undefined) return;

    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setRatio(img.naturalHeight / img.naturalWidth);
      }
    };

    return () => {
      img.onload = null;
    };
  }, [src, ratio]);

  return ratio;
}
